// client/music.ts — Théorie musicale + PARTITION générative par biome, PURE et testable.
// Aucun accès DOM ni Web Audio au chargement (comme death.ts) : ce module ne décide QUE des
// notes (hauteurs, rythme, durées, gains) ; c'est audio.ts qui les réalise en Web Audio.
// `OscillatorType` est un type DOM effacé à la compilation -> aucune dépendance runtime.
// 100 % cosmétique : jamais dans la simulation, le hash ni le rejeu (arpAt reçoit son aléa
// en paramètre, il reste donc pur et déterministe pour les tests).

import type { WorldKey } from '../shared/worlds'

/** Forme d'onde (type DOM, effacé au runtime). */
export type Wave = OscillatorType

/** Nombre de pas de la grille (double-croche) par mesure : 4 temps × 4. */
export const STEPS_PER_BAR = 16

/** MIDI -> Hz (tempérament égal, La4 = MIDI 69 = 440 Hz). Fonction PURE. */
export function midiToHz(midi: number): number {
  return 440 * Math.pow(2, (midi - 69) / 12)
}

/**
 * Recette musicale d'un biome. Les offsets d'accords/gammes sont en demi-tons RELATIFS à
 * `rootMidi`. Les gains sont linéaires et VOLONTAIREMENT bas : la musique est un lit sonore,
 * elle ne couvre jamais les SFX (voir le test de budget de volume dans tests/music.test.ts).
 */
export interface MusicSpec {
  /** Libellé (debug/lisibilité). */
  name: string
  /** Tempo en battements par minute. */
  bpm: number
  /** Note fondamentale (MIDI). */
  rootMidi: number
  /** Gamme = intervalles en demi-tons depuis la fondamentale (une octave). Réservoir mélodique. */
  scale: readonly number[]
  /** Progression d'accords : chaque accord = liste d'offsets (demi-tons) depuis rootMidi. */
  chords: readonly (readonly number[])[]
  /** Durée d'un accord de nappe, en mesures. */
  padBars: number
  padWave: Wave
  /** Désaccord de la seconde voix de nappe (cents) -> battement « moderne ». */
  padDetune: number
  /** Coupure du passe-bas de la nappe (Hz). */
  padCutoff: number
  /** Gain par OSCILLATEUR de nappe (2 par note). */
  padGain: number
  bassWave: Wave
  bassGain: number
  /** Pas (0..15, dans la mesure) où la basse frappe. */
  bassSteps: readonly number[]
  arpWave: Wave
  arpGain: number
  /** L'arpège sonne tous les N pas (0 = pas d'arpège). */
  arpEvery: number
  /** Décalage d'octave de l'arpège. */
  arpOctave: number
  percGain: number
  /** Pas (0..15) où la percussion (bruit filtré) frappe. */
  percSteps: readonly number[]
  percCutoff: number
  /** Dose de réverb propre au biome (0..1) -> profondeur/espace. */
  reverb: number
}

// Gammes réutilisées (intervalles depuis la fondamentale, une octave).
const MINOR = [0, 2, 3, 5, 7, 8, 10] as const        // mineur naturel (Aeolien)
const MAJOR = [0, 2, 4, 5, 7, 9, 11] as const        // majeur (Ionien)
const DORIAN = [0, 2, 3, 5, 7, 9, 10] as const       // mineur « ouvert »
const PHRYGIAN = [0, 1, 3, 5, 7, 8, 10] as const     // aride / hispanisant
const PENTA_MAJ = [0, 2, 4, 7, 9] as const           // pentatonique majeure (cristallin)
const HARM_MINOR = [0, 2, 3, 5, 7, 8, 11] as const   // mineur harmonique (inquiétant, 7e haussée)

/**
 * Partition par biome. Une COULEUR distincte chacun : gamme + tempo + timbre + espace.
 * Volumes bas partout (lit sonore). Voir le brief : classique=synthwave doux, prairie=pastoral,
 * grotte=ambient goutte, désert=modal aride, neige=cristallin aéré, usine=pulsation feutrée,
 * abysses=nappes profondes immergées, manoir=mineur inquiétant.
 */
export const MUSIC: Record<WorldKey, MusicSpec> = {
  // Classique — ville de nuit, SYNTHWAVE doux. La mineur, i–VI–III–VII (Am–F–C–G).
  classique: {
    name: 'Nocturne synthwave', bpm: 92, rootMidi: 57, scale: MINOR,
    chords: [[0, 3, 7], [-4, 0, 3], [3, 7, 10], [-2, 2, 5]], padBars: 1,
    padWave: 'sawtooth', padDetune: 8, padCutoff: 1400, padGain: 0.012,
    bassWave: 'sawtooth', bassGain: 0.034, bassSteps: [0, 8],
    arpWave: 'triangle', arpGain: 0.024, arpEvery: 2, arpOctave: 1,
    percGain: 0.015, percSteps: [4, 12], percCutoff: 3000, reverb: 0.5,
  },
  // Prairie — pastoral clair. Do majeur, I–V–vi–IV (C–G–Am–F), aérien, pas de percu.
  prairie: {
    name: 'Pastorale claire', bpm: 100, rootMidi: 60, scale: MAJOR,
    chords: [[0, 4, 7], [7, 11, 14], [9, 12, 16], [5, 9, 12]], padBars: 1,
    padWave: 'triangle', padDetune: 6, padCutoff: 2200, padGain: 0.013,
    bassWave: 'triangle', bassGain: 0.035, bassSteps: [0, 8],
    arpWave: 'triangle', arpGain: 0.03, arpEvery: 2, arpOctave: 1,
    percGain: 0, percSteps: [], percCutoff: 3000, reverb: 0.4,
  },
  // Grotte — ambient sombre / goutte. Ré Dorien, lent, nappe étouffée, ping rare très haut.
  grotte: {
    name: 'Ambient caverne', bpm: 68, rootMidi: 50, scale: DORIAN,
    chords: [[0, 3, 7], [7, 10, 14]], padBars: 2,
    padWave: 'sine', padDetune: 5, padCutoff: 700, padGain: 0.013,
    bassWave: 'sine', bassGain: 0.042, bassSteps: [0],
    arpWave: 'sine', arpGain: 0.02, arpEvery: 8, arpOctave: 2,
    percGain: 0, percSteps: [], percCutoff: 500, reverb: 0.8,
  },
  // Désert — modal aride. Mi Phrygien (bII signature), buzz de chaleur, tambour à main clairsemé.
  desert: {
    name: 'Modale aride', bpm: 84, rootMidi: 52, scale: PHRYGIAN,
    chords: [[0, 3, 7], [1, 5, 8]], padBars: 2,
    padWave: 'sawtooth', padDetune: 10, padCutoff: 1100, padGain: 0.012,
    bassWave: 'sawtooth', bassGain: 0.034, bassSteps: [0, 8],
    arpWave: 'triangle', arpGain: 0.022, arpEvery: 4, arpOctave: 1,
    percGain: 0.016, percSteps: [0, 6, 10], percCutoff: 520, reverb: 0.35,
  },
  // Neige — cristallin aéré. Do# pentatonique majeure, cloches sinus, basse minimale, beaucoup d'air.
  neige: {
    name: 'Cristal aéré', bpm: 76, rootMidi: 61, scale: PENTA_MAJ,
    chords: [[0, 4, 7], [5, 9, 12]], padBars: 2,
    padWave: 'sine', padDetune: 4, padCutoff: 3000, padGain: 0.011,
    bassWave: 'sine', bassGain: 0.028, bassSteps: [0],
    arpWave: 'sine', arpGain: 0.028, arpEvery: 2, arpOctave: 1,
    percGain: 0, percSteps: [], percCutoff: 3000, reverb: 0.75,
  },
  // Usine — pulsation industrielle FEUTRÉE. La mineur grave, basse en croches, tic métallique.
  usine: {
    name: 'Pulsation feutrée', bpm: 112, rootMidi: 45, scale: MINOR,
    chords: [[0, 3, 7], [8, 12, 15]], padBars: 1,
    padWave: 'square', padDetune: 7, padCutoff: 900, padGain: 0.011,
    bassWave: 'square', bassGain: 0.035, bassSteps: [0, 4, 8, 12],
    arpWave: 'square', arpGain: 0.02, arpEvery: 2, arpOctave: 2,
    percGain: 0.016, percSteps: [2, 6, 10, 14], percCutoff: 4000, reverb: 0.3,
  },
  // Abysses — nappes profondes immergées. Fa# mineur très grave, sub sinus, passe-bas serré, réverb longue.
  abysses: {
    name: 'Nappes immergées', bpm: 60, rootMidi: 42, scale: MINOR,
    chords: [[0, 3, 7], [3, 7, 10]], padBars: 2,
    padWave: 'sine', padDetune: 6, padCutoff: 500, padGain: 0.013,
    bassWave: 'sine', bassGain: 0.044, bassSteps: [0],
    arpWave: 'sine', arpGain: 0.018, arpEvery: 8, arpOctave: 2,
    percGain: 0, percSteps: [], percCutoff: 400, reverb: 0.85,
  },
  // Manoir hanté — mineur INQUIÉTANT. Ré mineur harmonique (C# haussé), boîte à musique désaccordée.
  manoir: {
    name: 'Manoir inquiétant', bpm: 72, rootMidi: 50, scale: HARM_MINOR,
    chords: [[0, 3, 7], [7, 11, 14]], padBars: 2,
    padWave: 'triangle', padDetune: 9, padCutoff: 1300, padGain: 0.012,
    bassWave: 'triangle', bassGain: 0.038, bassSteps: [0, 8],
    arpWave: 'triangle', arpGain: 0.022, arpEvery: 4, arpOctave: 2,
    percGain: 0, percSteps: [], percCutoff: 500, reverb: 0.6,
  },
}

/** Réduit un index dans [0, n) même pour un `i` négatif (modulo positif). PURE. */
function wrap(i: number, n: number): number {
  return ((i % n) + n) % n
}

/**
 * Index d'accord courant : la progression avance d'un cran toutes les `padBars` mesures (le
 * rythme HARMONIQUE = le rythme des nappes). Découplé de la mesure pour éviter l'aliasing quand
 * une progression courte est jouée sur des nappes longues. PURE.
 */
export function harmonyIndex(spec: MusicSpec, step: number): number {
  return Math.floor(step / (STEPS_PER_BAR * spec.padBars))
}

/** Accord courant au pas `step` (cycle sur la progression au rythme des nappes). PURE. */
export function chordAt(spec: MusicSpec, step: number): readonly number[] {
  return spec.chords[wrap(harmonyIndex(spec, step), spec.chords.length)]!
}

/** Fréquences (Hz) de la nappe au pas `step` : l'accord courant voisé depuis la fondamentale. PURE. */
export function padVoicing(spec: MusicSpec, step: number): number[] {
  return chordAt(spec, step).map((off) => midiToHz(spec.rootMidi + off))
}

/** Note de basse (Hz) au pas `step`, ou null (silence). Fondamentale de l'accord, une octave plus bas. PURE. */
export function bassAt(spec: MusicSpec, step: number): number | null {
  if (spec.bassGain <= 0) return null
  if (!spec.bassSteps.includes(wrap(step, STEPS_PER_BAR))) return null
  const root = chordAt(spec, step)[0]!
  return midiToHz(spec.rootMidi + root - 12)
}

/**
 * Note d'arpège (Hz) au pas `step`, ou null. Parcourt les notes de l'accord courant ; `rand`
 * (0..1, injecté -> reste PUR) glisse parfois vers une note de passage de la gamme (variation douce).
 */
export function arpAt(spec: MusicSpec, step: number, rand = 0): number | null {
  if (spec.arpEvery <= 0 || spec.arpGain <= 0) return null
  if (step % spec.arpEvery !== 0) return null
  const chord = chordAt(spec, step)
  const idx = Math.floor(step / spec.arpEvery)
  const off = rand > 0.8
    ? spec.scale[wrap(idx * 2, spec.scale.length)]!   // note de passage occasionnelle
    : chord[wrap(idx, chord.length)]!                  // note de l'accord (défaut)
  return midiToHz(spec.rootMidi + off + 12 * spec.arpOctave)
}

/** La percussion (bruit filtré) frappe-t-elle au pas `step` ? PURE. */
export function percAt(spec: MusicSpec, step: number): boolean {
  if (spec.percGain <= 0) return false
  return spec.percSteps.includes(wrap(step, STEPS_PER_BAR))
}

/**
 * Budget de gain SIMULTANÉ au pire cas d'un biome (toutes les voix au même instant) : nappe
 * (2 osc × notes de l'accord le plus fourni) + basse + arpège + percu. Sert de garde-fou de
 * volume, vérifié par les tests : ce lit sonore doit rester bien sous les SFX (≈ 0.15). PURE.
 */
export function peakGainBudget(spec: MusicSpec): number {
  const maxChord = spec.chords.reduce((m, c) => Math.max(m, c.length), 0)
  return spec.padGain * 2 * maxChord + spec.bassGain + spec.arpGain + spec.percGain
}
